import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getSystemSettings, updateSystemSettings, changeMyPassword } from '../utils/api';
import {
  Settings,
  Bell,
  Shield,
  CreditCard,
  Mail,
  Smartphone,
  Globe,
  Save
} from 'lucide-react';

const SettingsPage = () => {
  const { success, error } = useToast();
  const { logout, user } = useContext(AuthContext);
  const podeModo = user?.role === 'super_admin';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { error('A nova palavra-passe e a confirmação não coincidem'); return; }
    if (pw.next.length < 8 || !/[A-Z]/.test(pw.next) || !/[a-z]/.test(pw.next) || !/[0-9]/.test(pw.next)) {
      error('Mínimo 8 caracteres, com maiúscula, minúscula e número'); return;
    }
    setPwBusy(true);
    try {
      await changeMyPassword(pw.current, pw.next);
      setPw({ current: '', next: '', confirm: '' });
      success('Palavra-passe alterada! Vamos pedir que entre de novo com a nova.');
      // A sessão atual foi invalidada (RN-061) — reencaminhar para o login.
      setTimeout(() => { logout(); navigate('/'); }, 1800);
    } catch (err) {
      error(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Erro ao alterar a palavra-passe');
      setPwBusy(false);
    }
  };

  const { data: settings, isLoading, execute: fetchSettings } = useApi(getSystemSettings, { immediate: true });

  const [recruitmentGuardado, setRecruitmentGuardado] = useState(true);
  const [formData, setFormData] = useState({
    platformName: 'Dozy',
    platformEmail: 'suporte@dozy.com',
    homeClientFeeRate: '',
    retencaoGarantiaPct: '',
    limiarEmpresaKz: '',
    homeProviderFeeRate: '',
    minWithdrawal: 50,
    autoApproveWorkers: false,
    requireVerification: true,
    smsNotifications: true,
    emailNotifications: true,
    pushNotifications: true,
    maintenanceMode: false,
    recruitmentMode: true,
    maxRequestsPerWorker: 5,
    responseTimeLimit: 30,
    disputeAutoEscalation: 48,
    currency: 'AOA',
    language: 'pt-PT',
    timezone: 'Africa/Luanda',
  });

  // Sem isto o formulário mostrava sempre os valores por defeito e, ao gravar,
  // escrevia-os por cima do que estava guardado no servidor.
  useEffect(() => {
    if (settings && typeof settings === 'object') {
      setFormData(prev => ({ ...prev, ...settings }));
      if (typeof settings.recruitmentMode === 'boolean') {
        setRecruitmentGuardado(settings.recruitmentMode);
      }
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      // Gravar o resto das definições não pode reabrir ou fechar o
      // mercado. O modo só segue quando o interruptor mudou mesmo.
      if (payload.recruitmentMode === recruitmentGuardado) {
        delete payload.recruitmentMode;
      }
      await updateSystemSettings(payload);
      success('Configurações salvas com sucesso!');
      fetchSettings();
    } catch (err) {
      error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-white font-medium text-sm">{label}</p>
        <p className="text-gray-500 text-xs mt-0.5">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none
          ${checked ? 'bg-teal-500' : 'bg-gray-600'}`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform
            ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
    </div>
  );

  const InputField = ({ label, description, value, onChange, type = 'text', min, max, step }) => (
    <div className="py-3">
      <label className="block text-white font-medium text-sm mb-1">{label}</label>
      <p className="text-gray-500 text-xs mb-2">{description}</p>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) : e.target.value)}
        min={min}
        max={max}
        step={step}
        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                   focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      />
    </div>
  );

  const tabs = [
    { id: 'general', label: 'Geral', icon: Settings },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'payments', label: 'Pagamentos', icon: CreditCard },
    { id: 'security', label: 'Segurança', icon: Shield },
    { id: 'integrations', label: 'Integrações', icon: Globe },
  ];

  if (isLoading && !settings) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Configurações</h1>
          <p className="text-gray-400 text-sm mt-1">Gerencie as configurações da plataforma</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg 
                     font-medium transition-colors shadow-lg shadow-teal-500/20 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-1">
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                  ${activeTab === tab.id
                    ? 'bg-teal-500/10 text-teal-400 border-l-2 border-teal-500'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">

            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Configurações Gerais</h3>

                <div className="flex items-center justify-between gap-4 py-3 px-4 rounded-lg border border-teal-500/30 bg-teal-500/5">
                  <div>
                    <p className="text-white font-medium text-sm">Modo de recrutamento</p>
                    <p className="text-gray-400 text-xs mt-0.5 max-w-xl">
                      Ligado, a Dozy só recebe profissionais e empresas: pedidos, procura,
                      obras, pagamentos e Premium ficam fechados. Desligado, o mercado
                      de clientes volta a abrir, sem publicar código. A mudança fica na auditoria.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-sm font-semibold ${formData.recruitmentMode ? 'text-teal-300' : 'text-gray-100'}`}>
                      {formData.recruitmentMode ? 'Ligado' : 'Desligado'}
                    </span>
                    <button
                      type="button"
                      aria-label="Modo de recrutamento"
                      aria-pressed={Boolean(formData.recruitmentMode)}
                      disabled={!podeModo}
                      onClick={() => {
                        if (!podeModo) return;
                        if (formData.recruitmentMode) {
                          const ok = window.confirm('Abrir o mercado aos clientes agora?');
                          if (!ok) return;
                        }
                        setFormData({ ...formData, recruitmentMode: !formData.recruitmentMode });
                      }}
                      className={`relative shrink-0 w-11 h-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed
                        ${formData.recruitmentMode ? 'bg-teal-500' : 'bg-gray-300'}`}
                    >
                      <span
                        className={`absolute top-3.5 left-1 w-4 h-4 rounded-full transition-transform
                          ${formData.recruitmentMode ? 'translate-x-5 bg-white' : 'translate-x-0 bg-gray-900'}`}
                      />
                    </button>
                  </div>
                </div>

                <InputField
                  label="Nome da Plataforma"
                  description="Nome exibido na interface e emails"
                  value={formData.platformName}
                  onChange={(v) => setFormData({ ...formData, platformName: v })}
                />

                <InputField
                  label="Email de Suporte"
                  description="Email usado para comunicações com utilizadores"
                  value={formData.platformEmail}
                  onChange={(v) => setFormData({ ...formData, platformEmail: v })}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Idioma Padrão"
                    description="Idioma da plataforma"
                    value={formData.language}
                    onChange={(v) => setFormData({ ...formData, language: v })}
                  />
                  <InputField
                    label="Fuso Horário"
                    description="Fuso horário para agendamentos"
                    value={formData.timezone}
                    onChange={(v) => setFormData({ ...formData, timezone: v })}
                  />
                </div>

                <Toggle
                  label="Modo de Manutenção"
                  description="Ativar modo de manutenção (apenas admins podem aceder)"
                  checked={formData.maintenanceMode}
                  onChange={(v) => setFormData({ ...formData, maintenanceMode: v })}
                />

                <Toggle
                  label="Aprovação automática de profissionais"
                  description="Aprovar novos profissionais automaticamente"
                  checked={formData.autoApproveWorkers}
                  onChange={(v) => setFormData({ ...formData, autoApproveWorkers: v })}
                />

                <Toggle
                  label="Requerer Verificação de Documentos"
                  description="Exigir documentos para aprovação de profissionais"
                  checked={formData.requireVerification}
                  onChange={(v) => setFormData({ ...formData, requireVerification: v })}
                />
              </div>
            )}

            {/* Notifications */}
            {activeTab === 'notifications' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Configurações de Notificações</h3>

                <Toggle
                  label="Notificações por Email"
                  description="Enviar notificações por email"
                  checked={formData.emailNotifications}
                  onChange={(v) => setFormData({ ...formData, emailNotifications: v })}
                />

                <Toggle
                  label="Notificações por SMS"
                  description="Enviar notificações por SMS"
                  checked={formData.smsNotifications}
                  onChange={(v) => setFormData({ ...formData, smsNotifications: v })}
                />

                <Toggle
                  label="Notificações Push"
                  description="Enviar notificações push no app"
                  checked={formData.pushNotifications}
                  onChange={(v) => setFormData({ ...formData, pushNotifications: v })}
                />

                <div className="border-t border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-white mb-3">Limites de Tempo</h4>

                  <InputField
                    label="Tempo Limite de Resposta (minutos)"
                    description="Tempo máximo para profissional responder a um pedido"
                    value={formData.responseTimeLimit}
                    onChange={(v) => setFormData({ ...formData, responseTimeLimit: v })}
                    type="number"
                    min={5}
                    max={120}
                  />

                  <InputField
                    label="Escalonamento automático de mediações (horas)"
                    description="Tempo até mediação ser escalonada automaticamente"
                    value={formData.disputeAutoEscalation}
                    onChange={(v) => setFormData({ ...formData, disputeAutoEscalation: v })}
                    type="number"
                    min={1}
                    max={168}
                  />
                </div>
              </div>
            )}

            {/* Payments */}
            {activeTab === 'payments' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Configurações de Pagamento</h3>

                <InputField
                  label="Taxa Dozy ao cliente Home (%)"
                  description="Acrescida ao preço-base do serviço e apresentada separadamente antes do pagamento. Valor recomendado atual: 10%."
                  value={formData.homeClientFeeRate}
                  onChange={(v) => setFormData({ ...formData, homeClientFeeRate: v })}
                  type="number"
                  min={0}
                  max={50}
                  step={0.5}
                />

                <InputField
                  label="Taxa Dozy ao prestador Home (%)"
                  description="Deduzida proporcionalmente em cada libertação ao profissional. Valor recomendado atual: 2%. As transações antigas não são recalculadas."
                  value={formData.homeProviderFeeRate}
                  onChange={(v) => setFormData({ ...formData, homeProviderFeeRate: v })}
                  type="number"
                  min={0}
                  max={50}
                  step={0.5}
                />

                {/* Regras do Dozy Business.
                    Viviam em platform_settings sem ecrã nenhum: mudar a
                    retenção obrigava a mexer na base de dados à mão, que é
                    exactamente o que a plataforma não deve pedir a ninguém. */}
                <div className="pt-4 mt-4 border-t border-gray-800">
                  <h4 className="text-sm font-semibold text-gray-300 mb-3">Obras (Dozy Business)</h4>

                  <InputField
                    label="Retenção de garantia (%)"
                    description="De cada etapa aprovada fica retida esta percentagem, libertada ao empreiteiro quando o cliente aceitar a obra. Anuncia-se a quem concorre antes de fazer o preço, e congela no contrato: alterá-la não mexe em obras já contratadas."
                    value={formData.retencaoGarantiaPct}
                    onChange={(v) => setFormData({ ...formData, retencaoGarantiaPct: v })}
                    type="number"
                    min={0}
                    max={20}
                    step={0.5}
                  />

                  <InputField
                    label="Limiar de obra só para empresas (Kz)"
                    description="Acima deste valor, só empresas são convidadas a concorrer. Abaixo, um prestador individual com tecto suficiente também entra. Afecta apenas convites futuros."
                    value={formData.limiarEmpresaKz}
                    onChange={(v) => setFormData({ ...formData, limiarEmpresaKz: v })}
                    type="number"
                    min={0}
                    step={500000}
                  />
                </div>

                <InputField
                  label="Valor Mínimo de Levantamento (Kz)"
                  description="Valor mínimo que profissionais podem levantar"
                  value={formData.minWithdrawal}
                  onChange={(v) => setFormData({ ...formData, minWithdrawal: v })}
                  type="number"
                  min={10}
                  step={5}
                />

                <InputField
                  label="Moeda"
                  description="Moeda padrão da plataforma"
                  value={formData.currency}
                  onChange={(v) => setFormData({ ...formData, currency: v })}
                />

                <div className="border-t border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-white mb-3">Métodos de Pagamento</h4>

                  <Toggle
                    label="Cartão de Crédito/Débito"
                    description="Aceitar pagamentos por cartão"
                    checked={true}
                    onChange={() => {}}
                  />

                  <Toggle
                    label="PIX"
                    description="Aceitar pagamentos via PIX"
                    checked={true}
                    onChange={() => {}}
                  />

                  <Toggle
                    label="Boleto Bancário"
                    description="Aceitar pagamentos por boleto"
                    checked={false}
                    onChange={() => {}}
                  />
                </div>
              </div>
            )}

            {/* Security */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white mb-4">Segurança</h3>

                {/* Mudar a própria palavra-passe (RN-061) */}
                <form onSubmit={submitPassword} className="max-w-md space-y-3">
                  <h4 className="text-sm font-semibold text-white">Alterar palavra-passe</h4>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Palavra-passe atual</label>
                    <input type="password" autoComplete="current-password" required
                      value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Nova palavra-passe</label>
                    <input type="password" autoComplete="new-password" required
                      value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    <p className="text-gray-500 text-xs mt-1">Mínimo 8 caracteres, com maiúscula, minúscula e número.</p>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Confirmar nova palavra-passe</label>
                    <input type="password" autoComplete="new-password" required
                      value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <button type="submit" disabled={pwBusy}
                    className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors disabled:opacity-50 font-medium">
                    {pwBusy ? 'A alterar…' : 'Alterar palavra-passe'}
                  </button>
                  <p className="text-gray-500 text-xs">Ao alterar, todas as outras sessões são terminadas.</p>
                </form>

                <div className="border-t border-gray-700 pt-4 space-y-4">
                  <Toggle label="Bloqueio por Tentativas Falhadas"
                    description="5 tentativas → 30 min · 10 → 2 h · 20 → bloqueio até verificação (ativo)"
                    checked={true} onChange={() => {}} />
                  <Toggle label="Auditoria de acções críticas"
                    description="Login, mudanças de estado e pagamentos registados de forma imutável (ativo)"
                    checked={true} onChange={() => {}} />
                </div>
              </div>
            )}

            {/* Integrations */}
            {activeTab === 'integrations' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Integrações</h3>

                {[
                  { name: 'SMS', description: 'Fornecedor ainda em modo de simulação', icon: Smartphone, connected: false, status: 'Por ligar' },
                  { name: 'Geolocalização', description: 'Posições registadas pela aplicação Dozy', icon: Globe, connected: true, status: 'Disponível' },
                  { name: 'Gateway de pagamento', description: 'AppyPay e Banco Atlântico aguardam integração de produção', icon: CreditCard, connected: false, status: 'Por ligar' },
                  { name: 'Email', description: 'Depende das credenciais configuradas no servidor', icon: Mail, connected: false, status: 'Verificar servidor' },
                ].map((integration, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-600 rounded-lg">
                        <integration.icon className="w-5 h-5 text-gray-300" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{integration.name}</p>
                        <p className="text-gray-400 text-xs">{integration.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${integration.connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {integration.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
